import { useEffect, useMemo, useRef, useState } from 'react';

import { PlusCircleOutlined } from '@ant-design/icons';
import { App, Modal, Tag, theme } from 'antd';
import { useTranslation } from 'react-i18next';

import { ApiErrorData } from 'models/ApiResponse';
import { LeadCard, LeadTagWithCount } from 'models/Lead';

import { LeadService } from 'services/LeadService';

import {
  computeBulkPreview,
  countLeadsGainingTags,
} from 'utils/LeadTagGridHelper';

import appI18n from '../../../config/i18n';
import LeadTagPicker from './LeadTagPicker';

const I18N = 'scenes.leads.components.bulk_tag_modal';

interface BulkTagLeadsModalProps {
  open: boolean;
  /** Applied country filter: every selected lead shares this tag catalog. */
  country: string;
  /** Selected rows, in memory, used for the preview and the payload. */
  leads: LeadCard[];
  onClose: () => void;
  /** Called after a successful assignment (the modal also closes). */
  onSuccess: () => void;
}

function BulkTagLeadsModal({
  open,
  country,
  leads,
  onClose,
  onSuccess,
}: BulkTagLeadsModalProps) {
  const { t } = useTranslation(undefined, { i18n: appI18n });
  const { message } = App.useApp();
  const { token } = theme.useToken();

  const [chosen, setChosen] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  // Names seen in catalog searches: anything else the user typed is new.
  const [catalogNames, setCatalogNames] = useState<string[]>([]);
  const seenRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (open) {
      setChosen([]);
      setCatalogNames([]);
      seenRef.current = new Set();
    }
  }, [open]);

  const handleResults = (results: LeadTagWithCount[]) => {
    results.forEach((r) => seenRef.current.add(r.name));
    setCatalogNames(Array.from(seenRef.current));
  };

  const count = leads.length;
  const preview = useMemo(
    () =>
      computeBulkPreview(
        leads.map((l) => l.tags),
        chosen,
        catalogNames
      ),
    [leads, chosen, catalogNames]
  );

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const response = await LeadService.bulkAssignTags({
        country,
        userIds: leads.map((l) => l.id),
        tags: chosen,
      });
      if (response.success) {
        // Count from the selection, not the response: the response is per tag,
        // so it can't tell how many distinct leads actually gained something.
        const gaining = countLeadsGainingTags(
          leads.map((lead) => lead.tags),
          chosen
        );
        message.success(
          gaining > 0
            ? t(`${I18N}.success`, { count: gaining })
            : t(`${I18N}.success_none`, { count })
        );
        onSuccess();
        onClose();
      } else {
        const error = response.data as ApiErrorData;
        message.error((error.message as string) || t(`${I18N}.failed`), 3.5);
      }
    } catch (err) {
      message.error(t(`${I18N}.failed`), 3.5);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title={t(`${I18N}.title`)}
      // Mid-request the modal must stay: closing would orphan the result.
      onCancel={saving ? undefined : onClose}
      closable={!saving}
      keyboard={!saving}
      destroyOnClose
      maskClosable={false}
      width={520}
      okText={t(`${I18N}.submit`, { count })}
      cancelText={t(`${I18N}.cancel`)}
      onOk={handleSubmit}
      okButtonProps={{ disabled: chosen.length === 0, loading: saving }}
      cancelButtonProps={{ disabled: saving }}
    >
      <div style={{ color: token.colorTextSecondary, marginBottom: 12 }}>
        {t(`${I18N}.subtitle`, { count })}
      </div>
      <LeadTagPicker
        country={country}
        value={chosen}
        onChange={setChosen}
        isHighlighted={(name) =>
          preview.find((p) => p.name === name)?.isNew ?? false
        }
        onResults={handleResults}
      />
      <div
        style={{
          marginTop: 16,
          padding: '10px 12px',
          borderRadius: 6,
          background: token.colorFillQuaternary,
          border: `1px solid ${token.colorBorderSecondary}`,
        }}
      >
        <div
          style={{
            fontSize: 12,
            color: token.colorTextTertiary,
            marginBottom: 6,
          }}
        >
          {t(`${I18N}.preview_title`)}
        </div>
        {preview.length === 0 ? (
          <span style={{ color: token.colorTextTertiary }}>
            {t(`${I18N}.preview_empty`)}
          </span>
        ) : (
          preview.map((item) => {
            // The tag is the only chip in the row; everything that describes
            // what happens to it is plain text, so "new" never reads as a tag.
            const outcome =
              item.assignedCount === 0
                ? t(`${I18N}.preview_all_had`)
                : [
                    t(`${I18N}.preview_adds`, { count: item.assignedCount }),
                    item.alreadyHadCount > 0 &&
                      t(`${I18N}.preview_already_had`, {
                        count: item.alreadyHadCount,
                      }),
                  ]
                    .filter(Boolean)
                    .join(' · ');

            return (
              <div
                key={item.name}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  padding: '4px 0',
                }}
              >
                <Tag style={{ margin: 0, flex: 'none' }}>{item.name}</Tag>
                <span
                  style={{
                    fontSize: 12,
                    color: token.colorTextSecondary,
                    textAlign: 'right',
                  }}
                >
                  {item.isNew && (
                    <>
                      <PlusCircleOutlined style={{ marginRight: 4 }} />
                      {t(`${I18N}.preview_new`)}
                      {' · '}
                    </>
                  )}
                  {outcome}
                </span>
              </div>
            );
          })
        )}
      </div>
    </Modal>
  );
}

export default BulkTagLeadsModal;
